import * as React from 'react'
import { ReactElement } from 'react'
import { useFormikContext } from 'formik'
import { DateTime } from 'luxon'
import ReportForm, { ReportPeriod } from './ReportForm'
import BaseDropdown from '../../../../components/Dropdown/BaseDropdown'
import SortDropdown, { Direction } from '../../../../components/Dropdown/SortDropdown'
import { getDropdownProps } from '../../../../helpers/formHelper'
import { Toggle } from '@fluentui/react'
import { DocumentGenerationType } from '../../../../hooks/document/documentGenerationTypes'
import DateRange from '../../../DatePicker/DateRange'
import UserPicker, { UserPickerProps } from '../../../TagPicker/UserPicker'
import FormikComboboxField from '../../../Formik/FormikComboboxField'
import { ReportFormType } from '../../../../pages/Finance/Reports'

export enum EntityGroup {
    User = 'USER',
    FileOwner = 'FILE_OWNER',
    Debtor = 'DEBTOR',
}

export enum DebtorReportSort {
    Debtor = 'DEBTOR',
    Turnover = 'TURNOVER',
}

export enum PaidStatus {
    Paid = 'PAID',
    Unpaid = 'UNPAID',
    Both = 'BOTH',
}

export enum GroupingDate {
    Declaration = 'DECLARATION',
    Registration = 'REGISTRATION',
}

export type BilledTurnoverDto = {
    from: string
    to: string
    users: string[]
    cluster: ReportPeriod
    grouping: GroupingDate
    sorting: Direction
    byGroup: EntityGroup
    years: number
    order: DebtorReportSort
    paid: PaidStatus
    wip: boolean
}

function ReportBody(props: ReportFormType): ReactElement {
    const { errors, values, setFieldValue } = useFormikContext<BilledTurnoverDto>()

    return (
        <>
            <BaseDropdown
                {...getDropdownProps(values.byGroup, undefined, value => setFieldValue('byGroup', value), false)}
                required
                label={'Toon omzetten op basis van'}
                canClear={false}
                options={[
                    { key: EntityGroup.User, text: 'Uitvoerder' },
                    { key: EntityGroup.FileOwner, text: 'Verantwoordelijke' },
                    { key: EntityGroup.Debtor, text: 'Debiteur' },
                ]}
            />
            {values.byGroup !== EntityGroup.Debtor && (
                <>
                    <FormikComboboxField<BilledTurnoverDto, UserPickerProps>
                        orientation={props.orientation}
                        labelKey="file.filter.executive"
                        fieldName={'users'}
                        component={UserPicker}
                    />
                    <DateRange
                        startValue={values.from}
                        endValue={values.to}
                        changeStartDate={date => setFieldValue('from', date)}
                        changeEndDate={date => setFieldValue('to', date)}
                        orientation={props.orientation}
                        required
                        showLabel
                        validationMessage={errors.from || errors.to}
                    />
                    <BaseDropdown
                        {...getDropdownProps(values.cluster, undefined, value => setFieldValue('cluster', value), false)}
                        required
                        label={'Periodisering'}
                        canClear={false}
                        options={[
                            { key: ReportPeriod.Week, text: 'Week' },
                            { key: ReportPeriod.Month, text: 'Maand' },
                            { key: ReportPeriod.Year, text: 'Jaar' },
                        ]}
                    />
                    <SortDropdown {...getDropdownProps(values.sorting, undefined, value => setFieldValue('sorting', value), false)} />
                </>
            )}
            {values.byGroup === EntityGroup.Debtor && (
                <>
                    <BaseDropdown
                        {...getDropdownProps(values.years, undefined, value => setFieldValue('years', value), false)}
                        required
                        label={'Toon aantal jaren'}
                        canClear={false}
                        options={[
                            { key: 1, text: '1' },
                            { key: 2, text: '2' },
                            { key: 3, text: '3' },
                        ]}
                    />
                    <BaseDropdown
                        {...getDropdownProps(values.order, undefined, value => setFieldValue('order', value), false)}
                        required
                        label={'Sortering'}
                        canClear={false}
                        options={[
                            { key: DebtorReportSort.Debtor, text: 'Debiteur A-Z' },
                            { key: DebtorReportSort.Turnover, text: 'Omzet laatste jaar' },
                        ]}
                    />
                </>
            )}
            <BaseDropdown
                {...getDropdownProps(values.grouping, undefined, value => setFieldValue('grouping', value), false)}
                required
                label={'Werkzaamheden/Declaratiedatum'}
                canClear={false}
                options={[
                    { key: GroupingDate.Registration, text: 'Werkzaamheden' },
                    { key: GroupingDate.Declaration, text: 'Declaratiedatum' },
                ]}
            />
            <BaseDropdown
                {...getDropdownProps(values.paid, undefined, value => setFieldValue('paid', value), false)}
                required
                label={'Betaalde omzet'}
                canClear={false}
                options={[
                    { key: PaidStatus.Paid, text: 'Alleen betaald' },
                    { key: PaidStatus.Unpaid, text: 'Alleen niet betaald' },
                    { key: PaidStatus.Both, text: 'Betaald en niet betaald' },
                ]}
            />
            <Toggle
                label="Toon OHW"
                checked={values.wip}
                onText="Ja"
                offText="Nee"
                onChange={(_, checked?: boolean) => {
                    setFieldValue('wip', checked)
                }}
            />
        </>
    )
}

export default function BilledTurnoverReportForm(props: ReportFormType): ReactElement {
    return (
        <ReportForm<BilledTurnoverDto>
            title="Omzet"
            generationType={DocumentGenerationType.BilledTurnover}
            initialValues={{
                from: DateTime.local().minus({ months: 12 }).startOf('month').toISODate(),
                to: DateTime.local().toISODate(),
                users: [],
                cluster: ReportPeriod.Month,
                grouping: GroupingDate.Registration,
                sorting: Direction.Ascending,
                byGroup: EntityGroup.User,
                order: DebtorReportSort.Debtor,
                years: 3,
                paid: PaidStatus.Paid,
                wip: true,
            }}
            {...props}
        >
            <ReportBody {...props} />
        </ReportForm>
    )
}
